import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useContact } from '@/stores/contacts.store'
import { getEventDetailQueryOptions, listEventsQueryOptions, useEvent } from '@/stores/events.store'
import { useBooking } from '@/stores/bookings.store'
import { useNotes } from '@/stores/notes.store'
import { useAttachments } from '@/stores/attachments.store'
import { useTasksList } from '@/stores/tasks.store'
import { useInquiry } from '@/stores/leads.store'
import { organizationStore } from '@/stores/organization.store'
import { usePermissions } from '@/hooks/use-permissions'
import { seedEventDetailCaches } from '@/lib/detail-seeding'
import { connectTransport } from '@/lib/connect-transport'
import { useContactQuoteInteractionsByQuoteIds } from '@/stores/quotes.store'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { Eye, FileText } from 'lucide-react'
import {
  ContactHeader,
  BookingInfo,
  EventQuickActions,
  EventStageDropdown,
  BookingEventsCard,
  ActivityTabs,
  EventArchiveMenu,
} from '@/components/events'
import { Badge } from '@/components/ui/badge'
import { EventInvoicesCard } from '@/components/events/event-invoices-card'
import { EventInfoCard } from '@/components/events/id/event-info-card'
import { EventListCard } from '@/components/events/id/event-list-card'
import { SchedulingCard } from '@/components/events/id/scheduling-card'
import { BookingQuestionnaireCard } from '@/components/events/id/booking-questionnaire-card'
import { TimelineCard } from '@/components/events/id/timeline-card'
import { PackingListCard } from '@/components/events/id/packing-list-card'
import { DirectionsCard } from '@/components/events/id/directions-card'
import { CalendarDescriptionCard } from '@/components/events/id/calendar-description-card'
import { EventDetailErrorState } from '@/components/events/id/event-detail-error-state'
import {
  fadeVariants,
  instantVariants,
  slideVariants,
  type SlideDirection,
} from '@/lib/animation-variants'
import { isValidUUID } from '@/lib/uuid'
import { canPreloadOrganizationQueries } from '@/lib/route-preloading'

export const Route = createFileRoute('/_app/_home/events/$id')({
  component: EventDetailPage,
  validateSearch: z.object({
    from: z.string().optional(),
    details: z.string().optional(),
  }),
  loader: async ({ context, params }) => {
    if (!isValidUUID(params.id) || !canPreloadOrganizationQueries(context.auth)) return
    const { queryClient } = context

    // Resolve the aggregate before mounting entity hooks. A fire-and-forget
    // loader races useEvent/useBooking/useInquiry on cold navigation and
    // recreates the waterfall this aggregate exists to remove.
    const opts = getEventDetailQueryOptions(params.id)
    try {
      const resp = await queryClient.ensureQueryData(opts)
      const updatedAt = queryClient.getQueryState(opts.queryKey)?.dataUpdatedAt ?? Date.now()
      seedEventDetailCaches(queryClient, connectTransport, resp, updatedAt)
      if (resp.event?.bookingId) {
        void queryClient.prefetchQuery(
          listEventsQueryOptions({
            organizationId: organizationStore.state.selectedOrganizationId || '',
            bookingId: resp.event.bookingId,
          }),
        )
      }
    } catch {
      // The component's entity queries provide the user-visible error state.
    }
  },
})

/**
 * Links back to the lead/inquiry that produced this event's booking. Renders
 * nothing when the event has no originating inquiry (manually created, or
 * still loading), since a dead link is worse than no link.
 */
export function EventLeadLink({ inquiryId }: { inquiryId?: string }) {
  if (!inquiryId) return null

  return (
    <Button asChild variant="outline" size="sm">
      <Link to="/leads/$id" params={{ id: inquiryId }}>
        <Eye className="h-4 w-4" />
        View Lead
      </Link>
    </Button>
  )
}

function EventDetailPage() {
  const { id } = Route.useParams()

  if (!isValidUUID(id)) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="flex-1 border border-dashed py-12">
          <EmptyMedia variant="icon">
            <FileText className="h-6 w-6" />
          </EmptyMedia>
          <EmptyDescription>We couldn't load this event right now.</EmptyDescription>
        </Empty>
      </div>
    )
  }

  return <EventDetailContent id={id} />
}

function EventDetailContent({ id }: { id: string }) {
  const { from, details: selectedEventId } = Route.useSearch()
  const navigate = useNavigate()
  const prefersReducedMotion = useReducedMotion()
  const [motionInput, setMotionInput] = useState<'keyboard' | 'pointer'>(
    'keyboard'
  )

  // Track previous selectedEventId to compute direction synchronously
  const prevSelectedEventIdRef = useRef<string | undefined>(undefined)

  // Compute direction synchronously during render
  const direction = useMemo<SlideDirection>(() => {
    const prev = prevSelectedEventIdRef.current
    let dir: SlideDirection = 'forward'

    if (selectedEventId && !prev) {
      dir = 'forward' // Going from overview to detail
    } else if (!selectedEventId && prev) {
      dir = 'back' // Going from detail back to overview
    }

    // Update ref for next render
    prevSelectedEventIdRef.current = selectedEventId

    return dir
  }, [selectedEventId])

  // Memoize breadcrumbs to prevent infinite loops
  const customBreadcrumbs = useMemo(() => {
    if (from === 'calendar') {
      return [
        { label: 'Home', href: '/home' },
        { label: 'Calendar', href: '/calendar' },
        { label: 'Event' }
      ]
    }
    if (from === 'dashboard') {
      return [
        { label: 'Home', href: '/home' },
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Event' }
      ]
    }
    return undefined
  }, [from])

  // Set breadcrumbs based on navigation source
  useBreadcrumbFromRoute(customBreadcrumbs)

  const { event, isLoading: isEventLoading, isError: isEventError, error: eventError } = useEvent(id)
  const { isEventStaff } = usePermissions(event?.organizationId ?? '')
  const { contact: fullContact } = useContact(event?.contact?.id)

  // Fetch the selected event if different from main event
  const { event: selectedEvent, isLoading: isSelectedEventLoading } = useEvent(
    selectedEventId && selectedEventId !== id ? selectedEventId : ''
  )

  // Use selected event or fall back to main event
  const displayedEvent = selectedEventId === id ? event : (selectedEvent || event)

  // Fetch booking data for the event
  const { booking, isLoading: isBookingLoading } = useBooking(event?.bookingId)
  const { inquiry: bookingInquiry, isLoading: isBookingInquiryLoading } = useInquiry(booking?.inquiryId, { enabled: !isEventStaff })
  const {
    interactions: bookingQuoteInteractions,
    totalCount: bookingQuoteInteractionCount,
    isLoading: quoteInteractionsLoading,
  } = useContactQuoteInteractionsByQuoteIds({
    contactId: event?.contact?.id,
    quoteIds: bookingInquiry?.quoteIds || [],
    limitPerQuote: 100,
    enabled: !isEventStaff,
  })

  // Fetch notes and tasks for the booking (not the individual event)
  const { notes } = useNotes({
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: event?.bookingId,
    enabled: !isEventStaff,
  })

  const { tasks } = useTasksList({
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: event?.bookingId,
    enabled: !isEventStaff,
  })

  const { attachments } = useAttachments({
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: event?.bookingId,
    enabled: !isEventStaff,
  })

  // The full-page skeleton/error state gate only on the event query. Booking is
  // loaded in parallel and its dependent cards degrade to card-level skeletons.
  const isLoading = isEventLoading
  const isError = isEventError
  const showQuoteInteractions = !!booking?.inquiryId
  const isQuoteInteractionsLoading = showQuoteInteractions && (isBookingInquiryLoading || quoteInteractionsLoading)

  const handleEventSelect = (eventId: string) => {
    navigate({
      to: '/events/$id',
      params: { id },
      search: { from, details: eventId },
      replace: true,
    })
  }

  const handleBackToOverview = () => {
    navigate({
      to: '/events/$id',
      params: { id },
      search: { from },
      replace: true,
    })
  }

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        {/* Header Skeleton */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <SkeletonBar className="h-8 w-48" />
              <SkeletonBar className="h-4 w-32" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <SkeletonBar className="h-4 w-4" />
              <SkeletonBar className="h-4 w-40" />
              <SkeletonBar className="h-6 w-6 rounded" />
            </div>
            <div className="flex items-center gap-2">
              <SkeletonBar className="h-4 w-4" />
              <SkeletonBar className="h-4 w-32" />
              <SkeletonBar className="h-6 w-6 rounded" />
            </div>
          </div>
        </div>

        {/* Content Grid Skeleton */}
        <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Event Information Card Skeleton */}
            <Card>
              <CardHeader>
                <SkeletonBar className="h-6 w-40" />
                <SkeletonBar className="h-4 w-56 mt-2" />
              </CardHeader>
              <CardContent className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <SkeletonBar className="h-4 w-4" />
                    <div className="space-y-1 flex-1">
                      <SkeletonBar className="h-3 w-20" />
                      <SkeletonBar className="h-4 w-32" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions Skeleton */}
            <div className="grid gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonBar key={i} className="h-10 w-full" />
              ))}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {/* Booking Events Card Skeleton */}
            <Card>
              <CardHeader>
                <SkeletonBar className="h-6 w-32" />
                <SkeletonBar className="h-4 w-48 mt-2" />
              </CardHeader>
              <CardContent>
                <div className="border border-dashed rounded-lg p-12">
                  <SkeletonBar className="h-5 w-5 mx-auto mb-2" />
                  <SkeletonBar className="h-4 w-32 mx-auto" />
                </div>
              </CardContent>
            </Card>

            {/* Activity Tabs Card Skeleton */}
            <Card>
              <CardHeader>
                <SkeletonBar className="h-6 w-24" />
                <SkeletonBar className="h-4 w-56 mt-2" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <SkeletonBar className="h-10 w-24" />
                    <SkeletonBar className="h-10 w-24" />
                    <SkeletonBar className="h-10 w-24" />
                  </div>
                  <div className="border border-dashed rounded-lg p-12">
                    <SkeletonBar className="h-5 w-5 mx-auto mb-2" />
                    <SkeletonBar className="h-4 w-32 mx-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (isError || !event) {
    return <EventDetailErrorState error={eventError} />
  }

  const contact = event.contact
  const isEventDetailView = !!selectedEventId

  const transitionVariants =
    motionInput === 'keyboard'
      ? instantVariants
      : prefersReducedMotion
        ? fadeVariants
        : slideVariants

  return (
    <div
      className="flex h-full min-w-0 flex-col gap-4 overflow-hidden p-4"
      onKeyDownCapture={() => setMotionInput('keyboard')}
      onPointerDownCapture={() => setMotionInput('pointer')}
    >
      <ContactHeader
        firstName={contact?.firstName}
        lastName={contact?.lastName}
        email={contact?.email}
        phoneNumber={contact?.phoneNumber}
        inquiryId={booking?.inquiryId}
        currentContactId={contact?.id}
        contact={fullContact}
        isArchived={!!contact?.archivedAt}
        actionDropdown={
          <div className="flex items-center gap-2">
            {event.archivedAt && <Badge variant="secondary">Archived</Badge>}
            <EventLeadLink inquiryId={bookingInquiry?.id} />
            <EventStageDropdown
              eventId={event.id}
              currentStage={event.stage}
              bookingId={event.bookingId}
            />
            <EventArchiveMenu event={event} />
          </div>
        }
      />

      <div className="relative flex-1 min-h-0 overflow-hidden">
        <AnimatePresence mode="sync" custom={direction} initial={false}>
          {!isEventDetailView ? (
            <motion.div
              key="booking-overview"
              custom={direction}
              variants={transitionVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="absolute inset-0 grid gap-4 lg:grid-cols-[380px_1fr]"
            >
              {/* Left Column - Booking Overview */}
              <div className="space-y-4 min-w-0 overflow-y-auto pr-2 pb-4">
                {isBookingLoading ? (
                  <Card>
                    <CardHeader>
                      <SkeletonBar className="h-6 w-40" />
                      <SkeletonBar className="h-4 w-56 mt-2" />
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <SkeletonBar className="h-4 w-4" />
                          <div className="space-y-1 flex-1">
                            <SkeletonBar className="h-3 w-20" />
                            <SkeletonBar className="h-4 w-32" />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ) : (
                  <BookingInfo booking={booking} />
                )}
                {!isBookingLoading && booking && (
                  <BookingQuestionnaireCard
                    organizationId={booking.organizationId}
                    bookingId={booking.id}
                    eventTypeKey={booking.eventTypeKey}
                    questionnaire={booking.questionnaire}
                  />
                )}
                <EventQuickActions event={event} bookingInvoiceIds={booking?.invoiceIds} inquiryId={booking?.inquiryId} />
              </div>

              {/* Right Column - Events & Activity */}
              <div className="space-y-4 min-w-0 overflow-y-auto pb-4">
                <BookingEventsCard
                  currentEventId={event.id}
                  bookingId={event.bookingId}
                  onEventSelect={handleEventSelect}
                />

                {isBookingLoading ? (
                  <Card>
                    <CardHeader>
                      <SkeletonBar className="h-6 w-32" />
                      <SkeletonBar className="h-4 w-48 mt-2" />
                    </CardHeader>
                    <CardContent>
                      <div className="border border-dashed rounded-lg p-12">
                        <SkeletonBar className="h-5 w-5 mx-auto mb-2" />
                        <SkeletonBar className="h-4 w-32 mx-auto" />
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <EventInvoicesCard
                    bookingId={event.bookingId}
                    contactId={event.contactId}
                    organizationId={event.organizationId}
                    booking={booking}
                  />
                )}

                <ActivityTabs
                  organizationId={event.organizationId}
                  parentType={ParentTypeEnum.PARENT_TYPE_BOOKING}
                  parentId={event.bookingId}
                  notes={notes}
                  tasks={tasks}
                  attachments={attachments}
                  quoteInteractions={bookingQuoteInteractions}
                  quoteInteractionCount={bookingQuoteInteractionCount}
                  quoteInteractionsLoading={isQuoteInteractionsLoading}
                  showQuoteInteractions={showQuoteInteractions}
                  customerEmail={contact?.email}
                  emailInquiryId={booking?.inquiryId}
                  emailContactId={contact?.id}
                  showWorkflowRuns
                  workflowSourceResourceId={event.bookingId}
                  workflowTriggerEntityType="BOOKING"
                  hideNotesTasksAttachments={isEventStaff}
                  isEventStaff={isEventStaff}
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="event-detail"
              custom={direction}
              variants={transitionVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="absolute inset-0 grid gap-4 lg:grid-cols-[380px_1fr]"
            >
              {/* Left Column - Event Info & List */}
              <div className="space-y-4 min-w-0 overflow-y-auto pr-2 pb-4">
                {isSelectedEventLoading ? (
                  <Card>
                    <CardContent className="py-8">
                      <SkeletonBar className="h-6 w-32 mx-auto" />
                    </CardContent>
                  </Card>
                ) : displayedEvent ? (
                  <EventInfoCard
                    event={displayedEvent}
                    bookingId={event.bookingId}
                    onBack={handleBackToOverview}
                  />
                ) : (
                  <Card>
                    <CardContent className="py-8">
                      <Empty>
                        <EmptyMedia variant="icon">
                          <FileText className="h-6 w-6" />
                        </EmptyMedia>
                        <EmptyDescription>Event not found</EmptyDescription>
                      </Empty>
                    </CardContent>
                  </Card>
                )}

                <EventListCard
                  bookingId={event.bookingId}
                  selectedEventId={selectedEventId}
                  onEventSelect={handleEventSelect}
                />
              </div>

              {/* Right Column - 2x2 Grid & Activity */}
              <div className="space-y-4 min-w-0 overflow-y-auto pb-4">
                {/* 2x2 Card Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {displayedEvent && <SchedulingCard event={displayedEvent} />}
                  {displayedEvent && <TimelineCard event={displayedEvent} />}
                  {displayedEvent && <PackingListCard event={displayedEvent} />}
                  {displayedEvent && <DirectionsCard event={displayedEvent} />}
                  {displayedEvent && <CalendarDescriptionCard event={displayedEvent} />}
                </div>

                <ActivityTabs
                  organizationId={event.organizationId}
                  parentType={ParentTypeEnum.PARENT_TYPE_BOOKING}
                  parentId={event.bookingId}
                  notes={notes}
                  tasks={tasks}
                  attachments={attachments}
                  quoteInteractions={bookingQuoteInteractions}
                  quoteInteractionCount={bookingQuoteInteractionCount}
                  quoteInteractionsLoading={isQuoteInteractionsLoading}
                  showQuoteInteractions={showQuoteInteractions}
                  customerEmail={contact?.email}
                  emailInquiryId={booking?.inquiryId}
                  emailContactId={contact?.id}
                  showWorkflowRuns
                  workflowSourceResourceId={event.bookingId}
                  workflowTriggerEntityType="BOOKING"
                  hideNotesTasksAttachments={isEventStaff}
                  isEventStaff={isEventStaff}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
